import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { createFlow } from '@polyai/stream/store/reducers/flows';
import { createNoCodeCondition } from '@polyai/stream/store/reducers/flows/actions';
import Input from '@polyai/ui/components/atoms/Input';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import useCreateFlowFunction from 'components/molecules/LexicalEditor/plugins/ActionPicker/hooks/useCreateFlowFunction';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import useFlowsState from 'screens/FlowDetails/hooks/useFlowsState';
import { getNewFlow } from 'screens/Flows/utils';
import { validateFlowName } from 'utils/nameValidators';

interface FlowPopupProps {
  onCreate?: (flow: { id: string; name: string }) => void;
  onCancel?: (reason?: string) => void;
}

interface FlowForm {
  name: string;
}

export const FlowPopup = ({ onCancel, onCreate }: FlowPopupProps) => {
  const serverDispatch = useServerDispatch();
  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
  } = useForm<FlowForm>({
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: '',
    },
  });

  const { flows } = useFlowsState();
  const [addFlowFunction] = useCreateFlowFunction();
  const { isFeatureOn } = useIsFeatureOn();

  const validate = (name: string) => validateFlowName(flows)(name);

  const onSubmit: SubmitHandler<FlowForm> = async data => {
    if (!isValid) {
      return;
    }

    const func = await addFlowFunction(data.name);

    if (isFeatureOn('no_code_flow')) {
      const { flowPayload, noCodeConditionPayload } = getNewFlow({
        isNoCodeEnabled: true,
        flows,
        preferredName: data.name,
      });

      await serverDispatch(
        createFlow(flowPayload),
        createNoCodeCondition(noCodeConditionPayload),
      );
    } else {
      const payload = getNewFlow({
        isNoCodeEnabled: false,
        flows,
        preferredName: data.name,
      });

      await serverDispatch(createFlow(payload));
    }

    onCreate?.(func);
  };

  return (
    <ConfirmationModal
      buttonText="Create"
      content={
        <Input
          {...register('name', {
            validate,
            required: {
              message: 'Please provide a step name',
              value: true,
            },
          })}
          aria-autocomplete="none"
          errors={
            errors.name && errors.name.message ? [errors.name.message] : []
          }
          label="Flow name"
          fullWidth
        />
      }
      size="md"
      subtitle="An empty flow will be created. Don't forget to configure your flow steps before trying out your assistant."
      title="Create flow"
      nested
      open
      skipCloseAndConfirm
      onCancelButtonClick={onCancel}
      onConfirmButtonClick={handleSubmit(onSubmit)}
    />
  );
};
